/**
 * Meeting registration
 * @see {@link @smfa/utils/src/common/products.js}
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { forEach, get, trim, isEmpty } from "lodash";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Grid,
  Paper,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Select,
  Container,
  Divider,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Restaurant as RestaurantIcon,
  AccessibilityNew as AccessibilityIcon,
  Celebration as CelebrationIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import {
  updateNullOfObjectValues,
  isEmail,
  objectToQueryString,
} from "../utils/helper";
import {
  registationSubmit,
  getExistingRegistation,
} from "../actions/pagecontent.actions";
import { createPaymentIntent } from "../actions/payment.actions";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { getIpAddress } from "@smfa/utils";
import { nanoid } from "nanoid";
import StripePaymentModal, {
  PaymentIntentLoader,
  processStripePaymentIntent,
} from "../components/Common/StripePayment";

import { CountrySelect } from "@user27828/shared-utils/client";
import { products } from "@smfa/utils/src/common/products";
import PageLayout from "../components/Common/PageLayout";
import { isMeetingPlanVisibleForUser } from "../utils/membershipAccess";
import PageBanner from "../components/Common/PageBanner";
import SignInSignUp from "../components/Common/SignInSignUp";

/**
 * Meeting registration component
 * @returns {JSX.Element}
 * @component
 */
const MeetingRegister = () => {
  const navigate = useNavigate();
  // Extract year from query string
  const [searchParams] = useSearchParams();
  const requestedMeetingYear = trim(searchParams.get("year"));
  const meetingYear = /^\d{4}$/.test(requestedMeetingYear)
    ? requestedMeetingYear
    : "";
  const registrationStorageKey = `smfa:meeting-registration:${meetingYear || "unknown"}`;

  // Initial form fields
  const formFields = {
    title: "Dr.",
    first_name: "",
    last_name: "",
    prefferd_name: "",
    degree: "",
    institution: "",
    city: "",
    state: "",
    country: "",
    email: "",
    phone_number: "",
    friday_night: "",
    saturday_night: "",
    dietary_restrictions: "",
    lactation_room: "",
    meeting_more: "",
    year: meetingYear,
  };

  // Keep one year-scoped idempotency token across refreshes and later visits.
  // The server independently enforces one registration per email/year.
  const storedRegistrationToken = useMemo(
    () => {
      try {
        return localStorage.getItem(registrationStorageKey) || nanoid();
      } catch (error) {
        log.warn("Meeting registration token storage is unavailable:", error);
        return nanoid();
      }
    },
    [registrationStorageKey],
  );
  const [registrationTokens, setRegistrationTokens] = useState({});
  const registrationToken =
    registrationTokens[registrationStorageKey] || storedRegistrationToken;
  const saveRegistrationToken = useCallback(
    (token) => {
      setRegistrationTokens((tokens) => ({
        ...tokens,
        [registrationStorageKey]: token,
      }));
    },
    [registrationStorageKey],
  );
  const user = useSelector((state) => state.user);
  const registrationFormRef = useRef(null); // This ref is for the container, for scrolling
  const actualFormRef = useRef(null); // New ref for the actual <form> element
  const [formData, setformData] = useState(formFields);
  const [formErrors, setFormErrors] = useState(
    updateNullOfObjectValues(formFields),
  );
  const [ipAddress, setIpAddress] = useState("");
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [submitProgress, setSubmitProgress] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState("");
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(registrationStorageKey, registrationToken);
    } catch (error) {
      log.warn("Unable to persist the meeting registration token:", error);
    }
  }, [registrationStorageKey, registrationToken]);

  useEffect(() => {
    setformData((currentFormData) => ({
      ...currentFormData,
      year: meetingYear,
    }));
  }, [meetingYear]);

  const handleChange = (e) => {
    setformData({ ...formData, [e.target.name]: e.target.value });
    setFormErrors({ ...formErrors, [e.target.name]: null });
  };

  const handlePhoneChange = (value) => {
    setformData({ ...formData, phone_number: value || "" });
    setFormErrors({ ...formErrors, phone_number: null });
  };

  const handleCountryChange = (value) => {
    setformData({ ...formData, country: value });
    setFormErrors({ ...formErrors, country: null });
  };

  // Get the request IP once. Registration state is loaded separately because
  // the year-scoped token can change without remounting this route.
  useEffect(() => {
    const _getIpAddress = async () => setIpAddress(await getIpAddress());
    _getIpAddress();
  }, []);

  useEffect(() => {
    let active = true;

    if (registrationToken) {
      getExistingRegistation({ token: registrationToken })
        .then((res) => {
          if (!active || !res.data.status) {
            return;
          }

          const registration = res.data.data.registration;
          if (String(registration.year) !== meetingYear) {
            // Recover if an older client accidentally persisted one token
            // under more than one year-scoped key.
            saveRegistrationToken(nanoid());
            return;
          }

          setformData((currentFormData) => {
            const newformData = { ...currentFormData, year: meetingYear };
            Object.keys(currentFormData).forEach((key) => {
              if (registration[key]) {
                newformData[key] = registration[key];
              }
            });

            return newformData;
          });
        })
        .catch((err) => {
          if (active) {
            log.debug("Error fetching existing registration:", err);
          }
        });
    }

    return () => {
      active = false;
    };
  }, [meetingYear, registrationToken, saveRegistrationToken]);

  // Pre-populate form with user data from Redux
  useEffect(() => {
    const formMap = {
      // form name => state.user.data.* path
      email: "email",
      first_name: "profile.first_name",
      last_name: "profile.last_name",
      phone_number: "profile.phone_number",
      institution: "profile.institution",
      country: "profile.country",
      city: "profile.city",
      state: "profile.state",
      degree: "profile.degree",
    };

    if (user?.data) {
      const newformData = { ...formData };
      let hasAssignments = false;
      forEach(formMap, (value, key) => {
        const userVal = get(user?.data, value, false);
        if (userVal) {
          newformData[key] = userVal;
          hasAssignments = true;
        }
      });
      // Set preferred name from first name if not already set
      if (newformData.first_name && !newformData.prefferd_name) {
        newformData.prefferd_name = newformData.first_name;
      }
      if (hasAssignments) {
        setformData({ ...formData, ...newformData });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = (plan_type, amount) => {
    setFormErrorMsg("");
    if (!formValidate()) {
      registrationFormRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setSubmitProgress(true);
    registationSubmit({
      ...formData,
      token: registrationToken,
      ip_address: ipAddress,
      plan_type,
      payment_amount: amount,
    })
      .then((res) => {
        if (res.data.status) {
          setFormErrorMsg("");
          const savedRegistrationToken =
            res.data.data.registration_token || registrationToken;
          if (savedRegistrationToken !== registrationToken) {
            saveRegistrationToken(savedRegistrationToken);
          }
          if (res.data.data.already_paid) {
            setSubmitProgress(false);
            setFormErrorMsg(
              "This registration has already been paid. Please contact support if you need to make a change.",
            );
            return;
          }
          loadClientSecret({
            reg_id: res.data.data.reg_id,
            amount,
            plan_type,
            registrationToken: savedRegistrationToken,
          });
        } else {
          setFormErrorMsg(res.data.message);
          setSubmitProgress(false);
        }
      })
      .catch((err) => {
        log.error("Error submitting registration:", err);
        setFormErrorMsg(
          err?.response?.data?.message ||
            "Something went wrong, please try again later.",
        );
        setSubmitProgress(false);
      });
  };

  const formValidate = () => {
    const requiredFields = [
      "title",
      "first_name",
      "last_name",
      "degree",
      "institution",
      "city",
      "state",
      "country",
      "email",
      "friday_night",
      "saturday_night",
      "phone_number",
    ];
    let errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
        isValid = false;
      }
    });

    if (!isEmail(formData.email)) {
      errors.email = "Invalid email address";
      isValid = false;
    }
    // Phone validation -
    if (!isValidPhoneNumber(formData.phone_number)) {
      errors.phone_number = "Phone number is invalid";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handlePayNow = (e, amt, plan_type) => {
    e.preventDefault();
    if (submitProgress) {
      return;
    }
    setPaymentAmount(amt);
    handleSubmit(plan_type, amt);
  };

  const handlePaymentDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    if (isPaymentProcessing) {
      return;
    }
    setPaymentDialog(false);
  };

  const loadClientSecret = ({
    reg_id,
    amount,
    plan_type,
    registrationToken: savedRegistrationToken = registrationToken,
  }) => {
    const paymentDetails = {
      amount,
      user_id: user?.data?.id || null,
      plan_type,
      linked_table: "registrations",
      linked_id: reg_id,
      email: formData.email, // Pass email for Stripe intent
      registration_token: savedRegistrationToken,
    };

    // Use the utility function from StripePayment.jsx
    processStripePaymentIntent({
      dispatch: null, // Not using Redux dispatch directly
      createPaymentIntentAction: () => createPaymentIntent(paymentDetails),
      paymentDetails,
      setLoading: setSubmitProgress,
      onSuccess: ({ clientSecret: newClientSecret, free_plan }) => {
        if (free_plan) {
          setSubmitProgress(false);
          navigate(
            `/payment-complete${objectToQueryString({
              payment_type: "register_member",
              registration_token: savedRegistrationToken,
              free_plan: true,
            })}`,
          );
        } else {
          setClientSecret(newClientSecret);
          setPaymentDialog(true);
        }
      },
      onError: (errorMessage) => {
        setFormErrorMsg(errorMessage || "Failed to initialize payment.");
      },
    });
  };

  const handlePaymentProcess = (d) => setIsPaymentProcessing(d);

  const signInSignUpDescription = (
    <>
      Sign in or Sign up to take advantage of member-only pricing, and to
      pre-populate most of your profile information.
    </>
  );
  const signInSignUpRedirect = `/register?year=${meetingYear}`;

  return (
    <>
      <PageLayout pageName="Registration" checkComingSoon={false}>
        <PageBanner
          dense
          mainTitle={`${meetingYear ? meetingYear + " " : ""} Annual Meeting Registration`}
          pageTitle="Registration"
          bannerImage={
            process.env.REACT_APP_IMAGE_URL + "/1719216928_appointment bg4.jpg"
          }
        />

        <Container maxWidth="lg" sx={{ py: 5 }} ref={registrationFormRef}>
          {/* Show sign in/up bar above form if not signed in */}
          {(!user?.data?.profile || isEmpty(user.data.profile)) && (
            <SignInSignUp
              description={signInSignUpDescription}
              redirect={signInSignUpRedirect}
            />
          )}

          {formErrorMsg && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {formErrorMsg}
            </Alert>
          )}

          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <form ref={actualFormRef}>
              {/* Personal Information */}
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Personal Information
              </Typography>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 2 }}>
                  <FormControl fullWidth error={!!formErrors.title}>
                    <InputLabel id="title-label">Title *</InputLabel>
                    <Select
                      labelId="title-label"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      label="Title *"
                    >
                      <MenuItem value="Dr.">Dr.</MenuItem>
                      <MenuItem value="Mr.">Mr.</MenuItem>
                      <MenuItem value="Mrs.">Mrs.</MenuItem>
                      <MenuItem value="Ms.">Ms.</MenuItem>
                    </Select>
                    {formErrors.title && (
                      <FormHelperText>{formErrors.title}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    error={!!formErrors.first_name}
                    helperText={formErrors.first_name}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <PersonIcon sx={{ color: "action.active", mr: 1 }} />
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    error={!!formErrors.last_name}
                    helperText={formErrors.last_name}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Preferred Name"
                    id="prefferd_name"
                    name="prefferd_name"
                    value={formData.prefferd_name}
                    onChange={handleChange}
                    error={!!formErrors.prefferd_name}
                    helperText={formErrors.prefferd_name}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Degrees *"
                    id="degree"
                    name="degree"
                    value={formData.degree}
                    onChange={handleChange}
                    error={!!formErrors.degree}
                    helperText={formErrors.degree}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <SchoolIcon sx={{ color: "action.active", mr: 1 }} />
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Institution *"
                    id="institution"
                    name="institution"
                    value={formData.institution}
                    onChange={handleChange}
                    error={!!formErrors.institution}
                    helperText={formErrors.institution}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <WorkIcon sx={{ color: "action.active", mr: 1 }} />
                        ),
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                <LocationIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Contact Information
              </Typography>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Email *"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <EmailIcon sx={{ color: "action.active", mr: 1 }} />
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth error={!!formErrors.phone_number}>
                    <Box
                      sx={{
                        "& .PhoneInput": {
                          display: "flex",
                          alignItems: "center",
                          border: formErrors.phone_number
                            ? "1px solid #d32f2f"
                            : "1px solid #ccc",
                          borderRadius: "4px",
                          p: 1,
                          pl: 0.5,
                        },
                        "& .PhoneInputInput": {
                          flex: 1,
                          border: "none",
                          outline: "none",
                          p: 1,
                        },
                      }}
                    >
                      <InputLabel
                        sx={{
                          position: "absolute",
                          top: "-14px",
                          left: 0,
                          bgcolor: "background.paper",
                          px: 0.5,
                          fontSize: "0.75rem",
                          color: formErrors.phone_number
                            ? "#d32f2f"
                            : "rgba(0, 0, 0, 0.6)",
                        }}
                      >
                        Phone Number *
                      </InputLabel>
                      <PhoneInput
                        international
                        placeholder="Enter phone number"
                        defaultCountry="US"
                        value={formData.phone_number}
                        onChange={handlePhoneChange}
                      />
                    </Box>
                    {formErrors.phone_number && (
                      <FormHelperText>{formErrors.phone_number}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="City *"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    error={!!formErrors.city}
                    helperText={formErrors.city}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="State/Province *"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    error={!!formErrors.state}
                    helperText={formErrors.state}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CountrySelect
                    id="country"
                    name="country"
                    label="Country *"
                    value={formData.country}
                    topCountries={["US"]}
                    onChange={handleCountryChange}
                    fullWidth
                    searchable={true}
                    variant="outlined"
                    error={!!formErrors.country}
                    helperText={formErrors.country}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Event Attendance Section */}
              <Typography variant="h6" gutterBottom>
                <CelebrationIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Event Attendance
              </Typography>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl error={!!formErrors.friday_night} fullWidth>
                    <FormLabel>
                      I plan to attend the Welcome Reception on Friday night *
                    </FormLabel>
                    <RadioGroup
                      row
                      name="friday_night"
                      value={formData.friday_night}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                    {formErrors.friday_night && (
                      <FormHelperText>{formErrors.friday_night}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl error={!!formErrors.saturday_night} fullWidth>
                    <FormLabel>
                      I plan to attend the Mixer on Saturday night *
                    </FormLabel>
                    <RadioGroup
                      row
                      name="saturday_night"
                      value={formData.saturday_night}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                    {formErrors.saturday_night && (
                      <FormHelperText>
                        {formErrors.saturday_night}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12 }}>
                  <FormControl error={!!formErrors.lactation_room} fullWidth>
                    <FormLabel>
                      I would like access to a lactation room
                    </FormLabel>
                    <RadioGroup
                      row
                      name="lactation_room"
                      value={formData.lactation_room}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                    {formErrors.lactation_room && (
                      <FormHelperText>
                        {formErrors.lactation_room}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Dietary Restrictions"
                    id="dietary_restrictions"
                    name="dietary_restrictions"
                    value={formData.dietary_restrictions}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <RestaurantIcon
                            sx={{
                              color: "action.active",
                              mr: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Additional Accessibility Requirements"
                    id="meeting_more"
                    name="meeting_more"
                    value={formData.meeting_more}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <AccessibilityIcon
                            sx={{
                              color: "action.active",
                              mr: 1,
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        ),
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Show sign in/up bar between form and fee area if not signed in */}
          {(!user?.data?.profile || isEmpty(user.data.profile)) && (
            <SignInSignUp
              description={signInSignUpDescription}
              redirect={signInSignUpRedirect}
            />
          )}

          {/* Payment Options Section */}
          <Box sx={{ my: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              {`${meetingYear ? meetingYear + " " : ""}`}Meeting Registration
              Fee
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              {products
                .filter(
                  (p) =>
                    p.category === "meeting" && get(p, "admin_only") !== true,
                )
                .filter((plan) => {
                  // If user is not logged in, hide the membership_active option
                  if (!user?.data && plan.plan_type === "meeting_member") {
                    return false;
                  }
                  return isMeetingPlanVisibleForUser(
                    plan.plan_type,
                    user?.data,
                  );
                })
                .map((plan) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.plan_type}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `5px solid ${plan.borderColor}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom align="center">
                        {plan.name}
                      </Typography>
                      <Box sx={{ textAlign: "center", my: 2 }}>
                        <Typography variant="h4" color="primary">
                          ${plan.price}
                        </Typography>
                      </Box>
                      <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                        {plan.description}
                      </Typography>
                      <Box sx={{ mt: "auto", pt: 2, textAlign: "center" }}>
                        <Button
                          variant="contained"
                          onClick={(e) =>
                            handlePayNow(e, plan.price, plan.plan_type)
                          }
                          disabled={submitProgress}
                          endIcon={<ArrowForwardIcon />}
                          fullWidth
                          color={plan.color}
                        >
                          {plan.buttonText || "Pay Now"}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Container>
      </PageLayout>
      {/* Replace the Dialog with StripePaymentModal */}
      <StripePaymentModal
        open={paymentDialog}
        onClose={handlePaymentDialogClose}
        clientSecret={clientSecret}
        amount={paymentAmount}
        onPaymentProcess={handlePaymentProcess}
        successParams={{
          payment_type: "register_member",
          registration_token: registrationToken,
        }}
        dialogTitle="Complete Your Registration Payment"
      />
      {/* Loading overlay for payment intent creation */}
      <PaymentIntentLoader
        show={submitProgress}
        title="Processing your registration..."
      />
    </>
  );
};

export default MeetingRegister;
